import React, { useState, useEffect } from 'react';
import { landlordAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { UserCog, Mail, Clock, CheckCircle, XCircle, Building2, Shield, Edit, Calendar, Loader2, Phone, UserPlus, Copy, Link } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { getInitials } from '../../lib/utils';

const DEFAULT_PERMISSIONS = {
  view_properties: true,
  edit_properties: false,
  create_properties: false,
  view_units: true,
  edit_units: false,
  create_units: false,
  view_leases: true,
  create_leases: false,
  edit_leases: false,
  activate_leases: false,
  terminate_leases: false,
  view_tenants: true,
  invite_tenants: false,
  verify_tenants: false,
  offboard_tenants: false,
  view_payments: true,
  verify_payments: false,
  view_expenses: true,
  manage_expenses: false,
  view_reports: true,
  export_reports: false,
  send_messages: true,
  view_settings: false,
  edit_settings: false,
};

const PERMISSION_GROUPS = [
  {
    name: 'Properties & Units',
    permissions: [
      { key: 'view_properties', label: 'View Properties' },
      { key: 'create_properties', label: 'Create Properties' },
      { key: 'edit_properties', label: 'Edit Properties' },
      { key: 'view_units', label: 'View Units' },
      { key: 'create_units', label: 'Create Units' },
      { key: 'edit_units', label: 'Edit Units' },
    ]
  },
  {
    name: 'Leases',
    permissions: [
      { key: 'view_leases', label: 'View Leases' },
      { key: 'create_leases', label: 'Create Leases' },
      { key: 'edit_leases', label: 'Edit Leases' },
      { key: 'activate_leases', label: 'Activate Leases' },
      { key: 'terminate_leases', label: 'Terminate Leases' },
    ]
  },
  {
    name: 'Tenants',
    permissions: [
      { key: 'view_tenants', label: 'View Tenants' },
      { key: 'invite_tenants', label: 'Invite Tenants' },
      { key: 'verify_tenants', label: 'Verify / Reject Tenants' },
      { key: 'offboard_tenants', label: 'Offboard Tenants (destructive)' },
    ]
  },
  {
    name: 'Payments',
    permissions: [
      { key: 'view_payments', label: 'View Payments' },
      { key: 'verify_payments', label: 'Verify Payments' },
    ]
  },
  {
    name: 'Expenses & Reports',
    permissions: [
      { key: 'view_expenses', label: 'View Expenses' },
      { key: 'manage_expenses', label: 'Manage Expenses' },
      { key: 'view_reports', label: 'View Reports' },
      { key: 'export_reports', label: 'Export Reports' },
    ]
  },
  {
    name: 'Communication & Settings',
    permissions: [
      { key: 'send_messages', label: 'Send Messages' },
      { key: 'view_settings', label: 'View Settings' },
      { key: 'edit_settings', label: 'Edit Settings' },
    ]
  },
];

function PermissionCheckboxes({ permissions, onChange, disabled = false }) {
  const handleChange = (key, checked) => {
    onChange({ ...permissions, [key]: checked });
  };

  return (
    <Accordion type="multiple" className="w-full" defaultValue={['Properties & Units', 'Payments']}>
      {PERMISSION_GROUPS.map((group) => (
        <AccordionItem key={group.name} value={group.name}>
          <AccordionTrigger className="text-sm font-medium">{group.name}</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-2 gap-2">
              {group.permissions.map((perm) => (
                <div key={perm.key} className="flex items-center space-x-2">
                  <Checkbox
                    id={`perm-${perm.key}`}
                    checked={permissions[perm.key] || false}
                    onCheckedChange={(checked) => handleChange(perm.key, checked)}
                    disabled={disabled}
                  />
                  <label htmlFor={`perm-${perm.key}`} className="text-sm cursor-pointer">{perm.label}</label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export default function LandlordPropertyManagers() {
  const [managers, setManagers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, manager: null });
  const [inviteDialog, setInviteDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [inviteData, setInviteData] = useState({
    email: '',
    name: '',
    permissions: { ...DEFAULT_PERMISSIONS },
    all_properties: true,
    property_ids: [],
    access_expires_at: '',
  });
  
  const [verifyData, setVerifyData] = useState({
    permissions: { ...DEFAULT_PERMISSIONS },
    all_properties: true,
    property_ids: [],
    access_expires_at: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [mgrRes, propRes, invRes] = await Promise.all([
        landlordAPI.getManagers(),
        landlordAPI.getProperties(),
        landlordAPI.getManagerInvitations(),
      ]);
      setManagers(mgrRes.data);
      setProperties(propRes.data);
      setInvitations(invRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteData.email || !inviteData.name) {
      toast.error('Please fill in name and email');
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await landlordAPI.inviteManager({
        email: inviteData.email,
        name: inviteData.name,
        permissions: inviteData.permissions,
        all_properties: inviteData.all_properties,
        property_ids: inviteData.all_properties ? [] : inviteData.property_ids,
        access_expires_at: inviteData.access_expires_at || null,
      });
      
      toast.success(response.data.message || 'Invitation sent successfully');
      
      // Show invite link if email wasn't sent
      if (response.data.invite_link && !response.data.email_sent) {
        navigator.clipboard.writeText(response.data.invite_link);
        toast.info('Invite link copied to clipboard');
      }
      
      setInviteDialog(false);
      setInviteData({
        email: '',
        name: '',
        permissions: { ...DEFAULT_PERMISSIONS },
        all_properties: true,
        property_ids: [],
        access_expires_at: '',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await landlordAPI.cancelManagerInvitation(invitationId);
      toast.success('Invitation cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel invitation');
    }
  };

  const handleRevoke = async (managerId) => {
    try {
      await landlordAPI.revokeManager(managerId);
      toast.success('Manager access revoked');
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  const handleUpdateManager = async () => {
    if (!editDialog.manager) return;
    
    try {
      setSubmitting(true);
      await landlordAPI.updateManager(editDialog.manager.manager_id, {
        name: editDialog.manager.user?.name || '',
        email: editDialog.manager.user?.email || '',
        permissions: verifyData.permissions,
        all_properties: verifyData.all_properties,
        property_ids: verifyData.all_properties ? [] : verifyData.property_ids,
        access_expires_at: verifyData.access_expires_at || null,
      });
      
      toast.success('Permissions updated successfully');
      setEditDialog({ open: false, manager: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setSubmitting(false);
    }
  };

  const countPermissions = (perms) => {
    if (!perms) return 0;
    return Object.values(perms).filter(v => v === true).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="landlord-managers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">Property Managers</h1>
          <p className="text-muted-foreground mt-1">Manage people who help you with your properties</p>
        </div>
        <Button onClick={() => setInviteDialog(true)} data-testid="invite-manager-btn">
          <Mail className="w-4 h-4 mr-2" />
          Invite Manager
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Managers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{managers.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pending Invitations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{invitations.filter(i => i.status === 'pending').length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Properties</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{properties.length}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue={invitations.length > 0 ? "invitations" : "active"} className="w-full">
        <TabsList>
          <TabsTrigger value="invitations" className="relative">
            <Mail className="w-4 h-4 mr-2" />
            Pending Invitations
            {invitations.filter(i => i.status === 'pending').length > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 px-1.5 min-w-[20px] bg-blue-100 text-blue-800">
                {invitations.filter(i => i.status === 'pending').length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="active">
            <UserCog className="w-4 h-4 mr-2" />
            Active Managers ({managers.length})
          </TabsTrigger>
        </TabsList>

        {/* Pending Invitations Tab */}
        <TabsContent value="invitations" className="mt-6">
          {invitations.filter(i => i.status === 'pending').length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Mail className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Pending Invitations</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  Invite property managers using the button above. They'll appear here until they accept.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {invitations.filter(i => i.status === 'pending').map((inv) => (
                <Card key={inv.invitation_id} className="border-2 border-blue-200 dark:border-blue-900 bg-blue-50/50 dark:bg-blue-950/20">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-12 w-12 border-2 border-blue-300">
                        <AvatarFallback className="bg-blue-100 text-blue-700">
                          {getInitials(inv.name || 'PM')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base truncate">{inv.name}</CardTitle>
                        <Badge variant="outline" className="mt-1 text-blue-700 border-blue-300 bg-blue-100">
                          <Mail className="w-3 h-3 mr-1" />
                          Invited
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="w-4 h-4 shrink-0" />
                      <span className="truncate">{inv.email}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Invited: {new Date(inv.created_at).toLocaleDateString()}
                    </div>
                    {inv.invitation_expires_at && (
                      <div className="text-xs text-muted-foreground">
                        Expires: {new Date(inv.invitation_expires_at).toLocaleDateString()}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-xs">
                      <Badge variant="outline" className="text-xs">
                        <Shield className="w-3 h-3 mr-1" />
                        {countPermissions(inv.permissions)} Permissions
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        <Building2 className="w-3 h-3 mr-1" />
                        {inv.all_properties ? 'All' : `${inv.property_ids?.length || 0}`} Properties
                      </Badge>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          const link = inv.invite_link || `${window.location.origin}/register/manager?token=${inv.token}`;
                          navigator.clipboard.writeText(link);
                          toast.success('Invite link copied to clipboard');
                        }}
                        data-testid={`copy-invite-${inv.invitation_id}`}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy Link
                      </Button>
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            data-testid={`cancel-invite-${inv.invitation_id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Cancel Invitation?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will cancel the invitation sent to {inv.name} ({inv.email}). They will no longer be able to register using this link.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Keep</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleCancelInvitation(inv.invitation_id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Cancel Invitation
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Active Managers Tab */}
        <TabsContent value="active" className="mt-6">
          {managers.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <UserCog className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Active Managers</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  Property managers you verify will appear here.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {managers.map((mgr) => (
                <Card key={mgr.manager_id} data-testid={`manager-${mgr.manager_id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-12 w-12">
                          <AvatarFallback className="bg-[#C85A42]/10 text-[#C85A42]">
                            {getInitials(mgr.user?.name || 'PM')}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 className="font-semibold">{mgr.user?.name || 'Unknown'}</h3>
                          <p className="text-sm text-muted-foreground">{mgr.user?.email}</p>
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-3 text-xs">
                      <Badge variant="outline">
                        <Building2 className="w-3 h-3 mr-1" />
                        {mgr.all_properties ? 'All Properties' : `${mgr.properties?.length || mgr.property_ids?.length || 0} Properties`}
                      </Badge>
                      <Badge variant="outline">
                        <Shield className="w-3 h-3 mr-1" />
                        {countPermissions(mgr.permissions)} Permissions
                      </Badge>
                      {mgr.access_expires_at && (
                        <Badge variant="outline">
                          <Calendar className="w-3 h-3 mr-1" />
                          Expires: {new Date(mgr.access_expires_at).toLocaleDateString()}
                        </Badge>
                      )}
                    </div>

                    <div className="flex gap-2 mt-4">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => {
                          setVerifyData({
                            permissions: mgr.permissions || { ...DEFAULT_PERMISSIONS },
                            all_properties: mgr.all_properties ?? true,
                            property_ids: mgr.property_ids || [],
                            access_expires_at: mgr.access_expires_at || '',
                          });
                          setEditDialog({ open: true, manager: mgr });
                        }}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit Permissions
                      </Button>
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="destructive">
                            <XCircle className="w-4 h-4 mr-1" />
                            Revoke Access
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Revoke Manager Access?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will remove {mgr.user?.name}'s access to your properties. They will no longer be able to manage any of your properties.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleRevoke(mgr.manager_id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Revoke Access
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Manager Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, manager: open ? editDialog.manager : null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Manager Permissions</DialogTitle>
            <DialogDescription>
              Update permissions for {editDialog.manager?.user?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Property Access</Label>
              <Select
                value={verifyData.all_properties ? "all" : "specific"}
                onValueChange={(value) => setVerifyData({ ...verifyData, all_properties: value === "all", property_ids: [] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Properties</SelectItem>
                  <SelectItem value="specific">Specific Properties Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {!verifyData.all_properties && (
              <div className="space-y-2">
                <Label>Select Properties</Label>
                <div className="border rounded-lg p-3 max-h-32 overflow-y-auto space-y-2">
                  {properties.map((prop) => (
                    <div key={prop.property_id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-prop-${prop.property_id}`}
                        checked={verifyData.property_ids.includes(prop.property_id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setVerifyData({ ...verifyData, property_ids: [...verifyData.property_ids, prop.property_id] });
                          } else {
                            setVerifyData({ ...verifyData, property_ids: verifyData.property_ids.filter(id => id !== prop.property_id) });
                          }
                        }}
                      />
                      <label htmlFor={`edit-prop-${prop.property_id}`} className="text-sm cursor-pointer">{prop.name}</label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Permissions
              </Label>
              <div className="border rounded-lg p-3">
                <PermissionCheckboxes
                  permissions={verifyData.permissions}
                  onChange={(perms) => setVerifyData({ ...verifyData, permissions: perms })}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, manager: null })}>
              Cancel
            </Button>
            <Button onClick={handleUpdateManager} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Edit className="w-4 h-4 mr-2" />}
              Update Permissions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Manager Dialog */}
      <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Invite Property Manager
            </DialogTitle>
            <DialogDescription>
              Send an invitation email to add a new property manager
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-name">Full Name *</Label>
                <Input
                  id="invite-name"
                  placeholder="John Doe"
                  value={inviteData.name}
                  onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email Address *</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="manager@example.com"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                />
              </div>
            </div>

            {/* Property Access */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Property Access</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="invite-all-properties"
                    checked={inviteData.all_properties}
                    onCheckedChange={(checked) => setInviteData({ ...inviteData, all_properties: checked })}
                  />
                  <label htmlFor="invite-all-properties" className="text-sm font-medium cursor-pointer">
                    Access to all properties
                  </label>
                </div>
                {!inviteData.all_properties && (
                  <div className="ml-6 space-y-2">
                    <Label className="text-sm">Select Properties:</Label>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border rounded-md p-2">
                      {properties.map((prop) => (
                        <div key={prop.property_id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`invite-prop-${prop.property_id}`}
                            checked={inviteData.property_ids.includes(prop.property_id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setInviteData({ ...inviteData, property_ids: [...inviteData.property_ids, prop.property_id] });
                              } else {
                                setInviteData({ ...inviteData, property_ids: inviteData.property_ids.filter(id => id !== prop.property_id) });
                              }
                            }}
                          />
                          <label htmlFor={`invite-prop-${prop.property_id}`} className="text-sm cursor-pointer truncate">
                            {prop.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Access Expiration */}
            <div className="space-y-2">
              <Label htmlFor="invite-expires">Access Expires (Optional)</Label>
              <Input
                id="invite-expires"
                type="date"
                value={inviteData.access_expires_at ? inviteData.access_expires_at.split('T')[0] : ''}
                onChange={(e) => setInviteData({ ...inviteData, access_expires_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
              />
              <p className="text-xs text-muted-foreground">Leave empty for permanent access</p>
            </div>

            {/* Permissions */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Permissions</Label>
              <PermissionCheckboxes
                permissions={inviteData.permissions}
                onChange={(perms) => setInviteData({ ...inviteData, permissions: perms })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
